import { useState, useEffect } from 'react';
import { SUBSTEPS } from '../constants/steps';
import { apiCall } from '../services/apiService';

const useRecommendationsForm = ({ initialStep = 0 } = {}) => {
    // Constants
    const initialFormData = {
      age: "", 
      breed: "", 
      foodTypes: [],
      likedProducts: [], 
      dislikedProducts: [],
      likedProteins: [], 
      dislikedProteins: [],
      likedTextures: [], 
      dislikedTextures: [],
      preferredBrands: [], 
      dislikedBrands: [],
      goals: [], 
      other: "",
      // Add the new exploration flags - set to true by default
      exploreNewProteins: true,
      exploreNewTextures: true,
      exploreNewBrands: true
    };
  
    // State
    const [step, setStep] = useState(initialStep);
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  
    // Substep states
    const [kittySubstep, setKittySubstep] = useState(0);
    const [goalsSubstep, setgoalsSubstep] = useState(0);
    const [preferencesSubstep, setPreferencesSubstep] = useState(0);
  
    // Form data
    const [formData, setFormData] = useState(initialFormData);
  
    // Validation errors
    const [ageError, setAgeError] = useState(false);
    const [foodTypeError, setFoodTypeError] = useState(false);
  
    // Load saved state from session storage
    useEffect(() => {
      const saved = sessionStorage.getItem('recommendationsState');
      if (saved) {
        try {
          const { 
            submitted, recommendations, selectedProductIndex, 
            formData, step, kittySubstep, goalsSubstep, preferencesSubstep 
          } = JSON.parse(saved);
          
          setSubmitted(submitted);
          setRecommendations(recommendations || []);
          setSelectedProductIndex(selectedProductIndex || 0);
          setFormData({...initialFormData, ...formData}); // Ensure all new fields exist
          if (typeof step === 'number') {
            setStep(Math.max(initialStep, step));
          } else {
            setStep(initialStep);
          }
          setKittySubstep(kittySubstep || 0);
          setgoalsSubstep(goalsSubstep || 0);
          setPreferencesSubstep(preferencesSubstep || 0);
        } catch (error) {
          console.error("Error restoring saved state:", error);
        }
      }
    }, []);
  
    // Fetch products on component mount
    useEffect(() => {
      fetchProducts();
    }, []);
    
    // Save state when recommendations are available
    useEffect(() => {
      if (submitted && recommendations.length) {
        sessionStorage.setItem('recommendationsState', JSON.stringify({
          submitted, recommendations, selectedProductIndex,
          formData, step, kittySubstep, goalsSubstep, preferencesSubstep
        }));
      }
    }, [submitted, recommendations, selectedProductIndex, formData, step, kittySubstep, goalsSubstep, preferencesSubstep]);
  
    // API Functions
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await apiCall('api/products');
        
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }
        
        const data = await response.json();
        setProductOptions(data || []); 
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
  
    const submitRecommendationsRequest = async () => {
      setLoading(true);
      try {
        const response = await apiCall('api/predict', {
          method: "POST",
          body: JSON.stringify(formData)
        });
        
        if (!response.ok) {
          throw new Error("Failed to get recommendations");
        }
        
        const data = await response.json();
        setRecommendations(data.recommendations || []);
        setSubmitted(true);
      } catch (error) {
        console.error("Error submitting recommendations request:", error);
        // Consider adding user-friendly error handling here
      } finally {
        setLoading(false);
      }
    };
  
    // Helpers
    const getCurrentSubsteps = () => {
      if (step === 1) return { substeps: SUBSTEPS.kitty, current: kittySubstep };
      if (step === 2) return { substeps: SUBSTEPS.goals, current: goalsSubstep };
      if (step === 3) return { substeps: SUBSTEPS.preferences, current: preferencesSubstep };
      return null;
    };
  
    const resetQuiz = () => {
      sessionStorage.removeItem('recommendationsState');
      setStep(initialStep);
      setKittySubstep(0);
      setgoalsSubstep(0);
      setPreferencesSubstep(0);
      setSubmitted(false);
      setRecommendations([]);
      setSelectedProductIndex(0);
      setFormData(initialFormData);
      setAgeError(false);
      setFoodTypeError(false);
    };
  
    // Form handlers
    const handleChange = (field, value) => {
      setFormData(prev => {
        const newFormData = { ...prev, [field]: value };
        
        // Exploration checkboxes are now always true by default
        // No longer auto-setting based on goals selection
        
        return newFormData;
      });
    };
  
    const handleFoodTypeToggle = (type) => {
      setFoodTypeError(false);
      setFormData(prev => ({
        ...prev,
        foodTypes: prev.foodTypes.includes(type)
          ? prev.foodTypes.filter(f => f !== type)
          : [...prev.foodTypes, type]
      }));
    };
  
    // Helper function to scroll to top
    const scrollToTop = () => {
      // Force scroll to top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // Also try to scroll the recommendations container if it exists
      const recommendationsContainer = document.querySelector('.recommendations-container');
      if (recommendationsContainer) {
        recommendationsContainer.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    };
  
    // Navigation
    const handleNext = () => {
          // Age validation
    if (step === 1 && kittySubstep === 0 && !formData.age) {
      setAgeError(true);
      return;
    }
      setAgeError(false);
      
      // Food type validation
      if (step === 2 && goalsSubstep === 0 && formData.foodTypes.length === 0) {
        setFoodTypeError(true);
        return;
      }
      setFoodTypeError(false);
      
             // Use setTimeout to ensure state updates complete before scrolling
       const scrollAfterStateUpdate = () => {
         setTimeout(scrollToTop, 200);
       };

                   // Substep progression
      if (step === 1 && kittySubstep < 1) { 
        setKittySubstep(kittySubstep + 1);
        scrollToTop(); // Immediate scroll
        scrollAfterStateUpdate();
        return; 
      }
       if (step === 2 && goalsSubstep < 1) { 
         setgoalsSubstep(goalsSubstep + 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
         return; 
       }
       if (step === 3 && preferencesSubstep < 3) { 
         setPreferencesSubstep(preferencesSubstep + 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
         return; 
       }
       
       // Final actions
       if (step === 3 && preferencesSubstep === 3) {
         submitRecommendationsRequest();
       } else {
         setStep(step + 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
       }
    };
  
         const handleBack = () => {
       const scrollAfterStateUpdate = () => {
         setTimeout(scrollToTop, 200);
       };

             if (step === 1 && kittySubstep > 0) { 
         setKittySubstep(kittySubstep - 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
         return; 
       }
       if (step === 2 && goalsSubstep > 0) { 
         setgoalsSubstep(goalsSubstep - 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
         return; 
       }
       if (step === 3 && preferencesSubstep > 0) { 
         setPreferencesSubstep(preferencesSubstep - 1);
         scrollToTop(); // Immediate scroll
         scrollAfterStateUpdate();
         return; 
       }
       setStep(Math.max(0, step - 1));
       scrollToTop(); // Immediate scroll
       scrollAfterStateUpdate();
    };
  
    const getNextButtonText = () => {
      if (step === 0) return "Start Quiz";
      if (step === 3 && preferencesSubstep === 3) return "Get Recommendations";
      return "Continue";
    };
  
    return {
      step,
      kittySubstep,
      goalsSubstep,
      preferencesSubstep,
      formData,
      loading,
      productOptions,
      recommendations,
      submitted,
      ageError,
      foodTypeError,
      
      // Methods
      getCurrentSubsteps,
      resetQuiz,
      handleChange,
      handleFoodTypeToggle,
      handleBack,
      handleNext,
      getNextButtonText,
      setFormData,
      setSubmitted
    };
  };
  
  export default useRecommendationsForm;